import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Home, ChevronDown, ChevronRight, Plus, Info, ArrowDown, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "@/context/LangContext";

// Roofing Simple Builder MVP — R1 + R2 (Feb 2026).
//
// This component adds two small, contractor-friendly pickers at the top of the
// estimate editor:
//   • Roof System picker (8 systems)
//   • Pricing Style picker for THIS estimate (5 styles, default "labor_package_materials")
//
// When the contractor picks "Labor package + materials" AND a roof system, a
// single-click "Add roofing labor package" button appears that inserts one
// pre-filled Slice F unit-priced line item (roofer-friendly label + helpful
// description + unit=square + qty/rate 0 for the contractor to fill in).
//
// Everything is additive:
//   • No estimate math is changed. The labor package uses existing Slice F
//     unit-pricing math: total = unit_quantity × unit_price.
//   • No line items are auto-created without an explicit contractor click.
//   • All the metadata (roof_system_type, estimate_pricing_style, trade,
//     detailed_mode_open) is scrubbed from every customer-facing surface
//     by customer_safety.FORBIDDEN_INTERNAL_FIELDS.
//
// R3+ (missed-cost prompts, suggested materials, AI staging, roll math,
// custom scope items, sell-vs-cost basis, trade-aware filtering) are NOT in
// this slice.

const ROOF_SYSTEMS = [
  { id: "asphalt_shingle", labelKey: "roofing_builder.system_asphalt_shingle", labor_package_name: "Shingle roof labor package", default_unit_type: "roofing_square", default_unit_display: "square" },
  { id: "tpo", labelKey: "roofing_builder.system_tpo", labor_package_name: "TPO roof system labor package", default_unit_type: "roofing_square", default_unit_display: "square" },
  { id: "standing_seam", labelKey: "roofing_builder.system_standing_seam", labor_package_name: "Standing seam metal roof labor package", default_unit_type: "roofing_square", default_unit_display: "square" },
  { id: "tile", labelKey: "roofing_builder.system_tile", labor_package_name: "Tile roof labor package", default_unit_type: "roofing_square", default_unit_display: "square" },
  { id: "mod_bit", labelKey: "roofing_builder.system_mod_bit", labor_package_name: "Modified bitumen roof labor package", default_unit_type: "roofing_square", default_unit_display: "square" },
  { id: "coating", labelKey: "roofing_builder.system_coating", labor_package_name: "Roof coating labor package", default_unit_type: "sq_ft", default_unit_display: "sq ft" },
  { id: "repair", labelKey: "roofing_builder.system_repair", labor_package_name: "Roof repair labor package", default_unit_type: "flat", default_unit_display: "flat" },
  { id: "other", labelKey: "roofing_builder.system_other", labor_package_name: "Roofing labor package", default_unit_type: "roofing_square", default_unit_display: "square" },
];

const PRICING_STYLES = [
  { id: "labor_package_materials", labelKey: "roofing_builder.style_labor_package_materials_label", descKey: "roofing_builder.style_labor_package_materials_desc" },
  { id: "all_in_per_square", labelKey: "roofing_builder.style_all_in_per_square_label", descKey: "roofing_builder.style_all_in_per_square_desc" },
  { id: "all_in_per_sqft", labelKey: "roofing_builder.style_all_in_per_sqft_label", descKey: "roofing_builder.style_all_in_per_sqft_desc" },
  { id: "flat_lump_sum", labelKey: "roofing_builder.style_flat_lump_sum_label", descKey: "roofing_builder.style_flat_lump_sum_desc" },
  { id: "detailed", labelKey: "roofing_builder.style_detailed_label", descKey: "roofing_builder.style_detailed_desc" },
];

const DEFAULT_STYLE = "";   // Slice 2B1 · no pricing style is preselected.

// Roofer-friendly helper text now lives in the onboarding_i18n dictionary
// (roofing_builder.labor_package_description_help) so it renders in the
// contractor's active UI language when the labor package is inserted. The
// contractor can still edit / clear it after insertion.

// ---- Roof-area extractor (Feb 2026 efficiency pass) --------------------
// Scans the project's structured measurements + notes + job description +
// voice transcript for a clear roof-area value and returns
// { quantity, sourceLabel } or null.
//
// Priority sources: structured measurements > typed_notes on estimate >
// project.typed_notes > project.job_description > project.voice_transcript
// > project.labor_notes / material_notes.
//
// Recognized patterns per source:
//   * "24 roofing squares" / "24 squares" / "24 sq" (word-boundary — must
//     NOT match "24 sq ft" which is caught by the sq-ft regex first)
//   * "2,400 sq ft" / "2400 square feet" / "2400 sf"
//   * Word numbers up to twenty-nine are supported ("twenty four squares").
//
// Conflict resolution: within a single source, if multiple different
// values are extracted they cancel out (function moves to the next
// source). Across sources, the FIRST clear source wins (structured
// measurements beat weaker notes) — matches user spec.
//
// Unit conversion: 100 sq ft = 1 roofing square. If the contractor's
// selected unit is roofing_square but only sq ft matches were found,
// convert only when the sq-ft value is a clean multiple of 100 (so we
// never invent decimals from a "2,417 sq ft" mention).
const _RX_SQFT = /(\d[\d,]*(?:\.\d+)?)\s*(?:sq(?:uare)?\s*(?:ft|feet)|sf)\b/gi;
const _RX_SQUARES = /(\d+(?:\.\d+)?)\s*(?:roofing\s+)?(?:squares?|sq)\b(?!\s*(?:ft|feet|f\b))/gi;
const _WORD_NUM = { one:1,two:2,three:3,four:4,five:5,six:6,seven:7,eight:8,nine:9,ten:10,eleven:11,twelve:12,thirteen:13,fourteen:14,fifteen:15,sixteen:16,seventeen:17,eighteen:18,nineteen:19,twenty:20,"twenty one":21,"twenty two":22,"twenty three":23,"twenty four":24,"twenty five":25,"twenty six":26,"twenty seven":27,"twenty eight":28,"twenty nine":29,thirty:30 };
const _RX_WORD_SQUARES = /\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty(?:[\s-](?:one|two|three|four|five|six|seven|eight|nine))?|thirty)\s+(?:roofing\s+)?squares?\b/gi;
const _stripDigits = (s) => parseFloat(String(s || "").replace(/,/g, ""));

// Extract a single unambiguous value from one text source. Returns the
// numeric value in the CHOSEN unit (either "squares" or "sqft") — the
// caller decides which unit it wants.
const _extractFromSource = (text, wantUnit) => {
  if (!text) return null;
  const sqftMatches = [];
  const squareMatches = [];
  let m;
  _RX_SQFT.lastIndex = 0;
  while ((m = _RX_SQFT.exec(text)) !== null) {
    const v = _stripDigits(m[1]);
    if (Number.isFinite(v) && v > 0) sqftMatches.push({ value: v, text: m[0] });
  }
  _RX_SQUARES.lastIndex = 0;
  while ((m = _RX_SQUARES.exec(text)) !== null) {
    const v = _stripDigits(m[1]);
    if (Number.isFinite(v) && v > 0) squareMatches.push({ value: v, text: m[0] });
  }
  _RX_WORD_SQUARES.lastIndex = 0;
  while ((m = _RX_WORD_SQUARES.exec(text)) !== null) {
    const key = m[1].replace(/[-\s]+/g, " ").toLowerCase();
    const n = _WORD_NUM[key];
    if (n) squareMatches.push({ value: n, text: m[0] });
  }
  const uniqueSquares = [...new Set(squareMatches.map((h) => h.value))];
  const uniqueSqft = [...new Set(sqftMatches.map((h) => h.value))];
  // Ambiguous within one source → skip.
  if (uniqueSquares.length > 1 || uniqueSqft.length > 1) return null;

  const squares = uniqueSquares[0];
  const sqft = uniqueSqft[0];
  const squaresText = squareMatches.find((h) => h.value === squares)?.text;
  const sqftText = sqftMatches.find((h) => h.value === sqft)?.text;

  if (wantUnit === "roofing_square") {
    if (squares !== undefined) {
      return {
        value: squares,
        sourceText: squaresText,
        sourceValue: squares,
        sourceUnit: "roofing_square",
        converted: false,
        conversionRule: null,
      };
    }
    if (sqft !== undefined && sqft % 100 === 0) {
      return {
        value: sqft / 100,
        sourceText: sqftText,
        sourceValue: sqft,
        sourceUnit: "sq_ft",
        converted: true,
        conversionRule: "sqft_to_roofing_square",
      };
    }
    return null;
  }
  if (wantUnit === "sq_ft") {
    if (sqft !== undefined) {
      return {
        value: sqft,
        sourceText: sqftText,
        sourceValue: sqft,
        sourceUnit: "sq_ft",
        converted: false,
        conversionRule: null,
      };
    }
    if (squares !== undefined) {
      return {
        value: squares * 100,
        sourceText: squaresText,
        sourceValue: squares,
        sourceUnit: "roofing_square",
        converted: true,
        conversionRule: "roofing_square_to_sqft",
      };
    }
    return null;
  }
  return null;
};

const extractRoofArea = (project, estimate, wantUnit) => {
  if (!wantUnit || (wantUnit !== "roofing_square" && wantUnit !== "sq_ft")) {
    return null; // no auto-prefill for flat / each / linear_ft — out of scope
  }
  const sources = [
    { label: "measurements",     text: (project?.measurements || "").trim(),      display: "job measurements",  key: "job_measurements" },
    { label: "estimate.notes",   text: (estimate?.notes || "").trim(),            display: "estimate notes",    key: "estimate_notes" },
    { label: "typed_notes",      text: (project?.typed_notes || "").trim(),       display: "typed notes",       key: "typed_notes" },
    { label: "job_description",  text: (project?.job_description || "").trim(),   display: "job description",   key: "job_description" },
    { label: "voice_transcript", text: (project?.voice_transcript || "").trim(),  display: "job walk notes",    key: "job_walk_notes" },
    { label: "labor_notes",      text: (project?.labor_notes || "").trim(),       display: "labor notes",       key: "labor_notes" },
    { label: "material_notes",   text: (project?.material_notes || "").trim(),    display: "material notes",    key: "material_notes" },
  ];
  for (const s of sources) {
    if (!s.text) continue;
    const hit = _extractFromSource(s.text, wantUnit);
    if (hit) {
      return {
        quantity: Math.round(hit.value * 100) / 100,
        sourceLabel: s.display,
        sourceKey: s.key,
        sourceText: hit.sourceText,
        sourceValue: hit.sourceValue,
        sourceUnit: hit.sourceUnit,
        converted: hit.converted,
        conversionRule: hit.conversionRule,
      };
    }
  }
  return null;
};

// Slice 2B1 — Map a PricingRule's selling_price_unit onto the extractor's
// internal want_unit enum. Returns null for non-area units (lf, item)
// where roof-area prefill does not apply.
const mapRuleUnitToWant = (ruleUnit) => {
  const u = String(ruleUnit || "").trim().toLowerCase();
  if (["sqft", "sq_ft", "sq ft", "square feet", "sf"].includes(u)) return "sq_ft";
  if (["square", "roofing_square", "sq"].includes(u)) return "roofing_square";
  return null;
};
// -------------------------------------------------------------------------

const RoofingSimpleBuilder = ({ project, estimate, setEstimate, insertLine }) => {
  const { t } = useLang();
  // Contractor-facing labor-package description template. Pulled per active
  // UI language when the contractor clicks "Add roofing labor package" so
  // the seed description matches the contractor's current preference. Once
  // saved onto the line, it becomes contractor-owned content (editable) and
  // is not re-translated by us.
  const laborPackageHelp = t("roofing_builder.labor_package_description_help");
  // Hooks first (React rules-of-hooks: never conditional / never early-return
  // before every hook has been called). The visibility guard runs below.
  const hasSavedRoofChoice = !!(estimate?.roof_system_type || estimate?.estimate_pricing_style);
  const [open, setOpen] = useState(hasSavedRoofChoice);

  // Slice 2B1 — Per-rule in-flight guard. React 18 StrictMode double-
  // invokes state updaters and (in some browsers) synthetic events can
  // fire the click handler twice for a single physical click when the
  // parent card re-renders synchronously mid-handler. This ref pins the
  // handler to run its INSERTION path exactly once per rule per short
  // window — regardless of how many times the handler is invoked. It
  // does NOT hide the root cause of a double-invoke; it is belt to the
  // functional-updater dedupe braces inside `insertLine`.
  const inFlightRulesRef = useRef(new Set());

  // Slice 2B1 · One source of truth. Load the tenant's approved
  // reusable PricingRules for the Roofing trade from Trade Setup.
  // Declared here (before any conditional early return) so React's
  // rules-of-hooks are respected on every render path.
  const [approvedSystems, setApprovedSystems] = useState([]);
  const [approvedLoading, setApprovedLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/onboarding/trade-setup/estimate-picker/Roofing");
        if (alive) setApprovedSystems(data.systems || []);
      } catch { if (alive) setApprovedSystems([]); }
      finally { if (alive) setApprovedLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Cleanup Slice B — v2 (Feb 2026 · post-Rio dry-run finding #1).
  //
  // Previous version used a keyword BLACKLIST — it only hid the card when
  // project.job_type matched a known non-roofing keyword (kitchen, flooring,
  // paint, etc.). Anything unrecognized ("LVP install", "Vinyl plank
  // replace", a novel trade label) fell through and rendered the roofing
  // builder on a flooring estimate. Rio's dry-run caught exactly this: an
  // "LVP flooring installation" estimate was showing the Roofing Simple
  // Builder at the top.
  //
  // v2 flips the guard to a WHITELIST that fails safe. The card renders in
  // only three cases:
  //   (a) project.job_type positively signals a roofing job
  //       (roof / reroof / shingle / tpo / standing seam / modified
  //       bitumen / epdm / torch down).
  //   (b) the estimate has already committed a roof_system_type OR
  //       estimate_pricing_style — keeps the picks reachable on reload
  //       even if the contractor edits the job_type afterwards.
  //   (c) job_type is blank / whitespace — brand-new estimate case, keep
  //       the card visible (collapsed by default) so the contractor can
  //       still opt-in before naming the job.
  //
  // Anything else — flooring, kitchen, bathroom, drywall, painting,
  // plumbing, HVAC, siding, or any other clearly non-roofing trade — is
  // hidden entirely. Same source (project.job_type) that powers the
  // estimate header/subtitle, so the UI is internally consistent.
  const jobType = (project?.job_type || "").toLowerCase().trim();
  const ROOFING_SIGNAL_RE =
    /\b(roof|reroof|re-roof|shingle|tpo|standing\s?seam|mod(?:ified)?\s?bit(?:umen)?|epdm|torch\s?down)\b/;
  const looksRoofing = ROOFING_SIGNAL_RE.test(jobType);
  if (jobType && !looksRoofing && !hasSavedRoofChoice) {
    return null;
  }


  const roofSystemId = estimate?.roof_system_type || "";
  const pricingStyle = estimate?.estimate_pricing_style || "";
  const system = ROOF_SYSTEMS.find((s) => s.id === roofSystemId) || null;

  const setField = (patch) => setEstimate((prev) => ({ ...prev, ...patch }));

  // Find the index of an existing labor-package line matching the selected
  // roof system (exact name match). Returns -1 when none. Used to (a) turn
  // the Add button into a "Jump to line" button after the labor package is
  // already added, and (b) prevent silent duplicates.
  const existingLaborPackageIdx = (() => {
    if (!system) return -1;
    const lines = estimate?.line_items || [];
    for (let i = 0; i < lines.length; i++) {
      if (lines[i]?.name === system.labor_package_name) return i;
    }
    return -1;
  })();
  const alreadyAdded = existingLaborPackageIdx >= 0;

  // Scroll to a line card by index + briefly highlight it. Runs after React
  // has committed the DOM update.
  const jumpToLine = (idx) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="line-card-${idx}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("jc-roof-highlight");
      setTimeout(() => el.classList.remove("jc-roof-highlight"), 2400);
    }, 60);
  };

  const addLaborPackage = () => {
    if (!system) return;
    if (alreadyAdded) {
      // Never silently duplicate — jump to the existing labor package line.
      jumpToLine(existingLaborPackageIdx);
      toast.info(t("roofing_builder.toast_already_added", system.labor_package_name));
      return;
    }
    // Attempt to pre-fill roof area / labor units from existing project + estimate
    // notes. Pricing is NEVER pre-filled — contractor still enters the rate.
    const prefill = extractRoofArea(project, estimate, system.default_unit_type);
    const preset = {
      name: system.labor_package_name,
      description: laborPackageHelp,
      // Slice F unit-pricing shape — total = unit_quantity × unit_price.
      pricing_method: "unit_price",
      unit_type: system.default_unit_type,
      unit_quantity: prefill ? prefill.quantity : 0,
      unit_price: 0,
      // Keep the legacy line fields sane so existing rendering + Double Check
      // + PDF generation don't hiccup. Backend _normalize_line_items_for_pricing_method
      // mirrors unit_quantity/unit_type onto quantity/unit and computes total.
      quantity: prefill ? prefill.quantity : 0,
      unit: system.default_unit_display,
      material_basis_unit: system.default_unit_display,
      material_qty: 0,
      material_cost: 0,
      material_required: false,
      labor_basis: "hour",
      labor_qty: 0,
      labor_unit_cost: 0,
      labor_hours: 0,
      labor_cost: 0,
      // No extra markup — the roofer's rate IS their sell price.
      markup_pct: 0,
      total: 0,
      needs_pricing: false,
      needs_material_pricing: false,
      // Contractor-internal breadcrumb explaining WHERE the qty came from —
      // scrubbed from customer-facing surfaces by customer_safety filter
      // (any unknown key on a line is not touched by the scrub list, but
      // this field name is unique enough to be safe and is only rendered
      // inside the pricing-method block, never on PDFs).
      _prefill_source: prefill ? prefill.sourceLabel : null,
      _prefill_converted: prefill ? prefill.converted : false,
    };
    insertLine(preset);
    // After insertion the line lives at end-of-array. Scroll to it and toast.
    const newIdx = (estimate?.line_items || []).length;
    jumpToLine(newIdx);
    if (prefill) {
      toast.success(
        t(
          "roofing_builder.toast_added_with_prefill",
          system.labor_package_name,
          prefill.quantity,
          system.default_unit_display,
          prefill.sourceLabel,
          prefill.converted,
        ),
        { duration: 5000 }
      );
    } else {
      toast.success(
        t("roofing_builder.toast_added_no_prefill", system.labor_package_name),
        { duration: 4200 }
      );
    }
  };

  return (
    <section className="jc-card p-5" data-testid="roofing-simple-builder">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="roofing-builder-toggle"
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-3 text-left"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Home size={16} className="text-[var(--jc-blue)]" />
            <div className="overline">{t("roofing_builder.overline")}</div>
          </div>
          <div className="font-display text-lg mt-1">
            {t("roofing_builder.title")}
          </div>
          <p className="text-xs text-[var(--jc-text-soft)] mt-1 leading-relaxed">
            {t("roofing_builder.helper")}
          </p>
          {(system || pricingStyle) && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {system && (
                <span
                  data-testid="roofing-builder-system-chip"
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-[var(--jc-blue)]/10 text-[var(--jc-blue)]"
                >
                  {t(system.labelKey)}
                </span>
              )}
              {pricingStyle && (
                <span
                  data-testid="roofing-builder-style-chip"
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-[var(--jc-teal)]/10 text-[var(--jc-teal)]"
                >
                  {(() => {
                    const s = PRICING_STYLES.find((p) => p.id === pricingStyle);
                    return s ? t(s.labelKey) : pricingStyle;
                  })()}
                </span>
              )}
            </div>
          )}
        </div>
        {open
          ? <ChevronDown size={18} className="mt-1 text-[var(--jc-text-soft)] shrink-0" />
          : <ChevronRight size={18} className="mt-1 text-[var(--jc-text-soft)] shrink-0" />}
      </button>

      {open && (
        <div className="mt-5 space-y-5" data-testid="roofing-builder-body">
          {/* Slice 2B1 · Approved reusable systems from Trade Setup.
              Single source of truth for pricing; the generic list below
              is retained only as a filter for the labor-package helper. */}
          <div data-testid="roofing-approved-systems">
            <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-2">
              {t("roofing_builder.approved_label")}
            </label>
            {approvedLoading && (
              <div className="text-xs text-[var(--jc-text-soft)]">{t("roofing_builder.approved_loading")}</div>
            )}
            {!approvedLoading && approvedSystems.length === 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                   data-testid="roofing-approved-empty">
                {t("roofing_builder.approved_empty_prefix")}
                <a href="/app/setup?step=trade_setup"
                   className="ml-1 underline font-semibold"
                   data-testid="roofing-approved-empty-link">
                  {t("roofing_builder.approved_empty_link")}
                </a>{t("roofing_builder.approved_empty_suffix")}
              </div>
            )}
            {!approvedLoading && approvedSystems.length > 0 && (
              <ul className="space-y-2" data-testid="roofing-approved-list">
                {approvedSystems.map((s) => (
                  <li key={s.pricing_rule_id}
                      className="rounded-md border border-[var(--jc-border-strong)] p-3"
                      data-testid={`roofing-approved-item-${s.pricing_rule_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                          <div className="font-semibold text-sm">{s.system_type || s.name}</div>
                        </div>
                        <div className="text-[11px] text-[var(--jc-text-soft)] mt-0.5">
                          {s.pricing_type === "installed_selling" && s.selling_price_per_unit != null && (
                            <>{t("roofing_builder.approved_price_prefix")}<b className="text-[var(--jc-text)]">${Number(s.selling_price_per_unit).toFixed(2)}</b> {t("roofing_builder.approved_price_per", s.selling_price_unit)}</>
                          )}
                          {s.pricing_type === "fixed_price" && s.is_flat_price && (
                            <>{t("roofing_builder.approved_flat_prefix")}<b className="text-[var(--jc-text)]">${Number(s.flat_price_amount || 0).toFixed(2)}</b></>
                          )}
                          {s.pricing_type !== "installed_selling" && s.pricing_type !== "fixed_price" && (
                            <>{t("roofing_builder.approved_reusable_prefix")}{s.pricing_type}</>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          // Defensive: stop bubbling so no ancestor click
                          // handler (or accidental future ancestor click
                          // handler) can re-invoke this insertion path.
                          e.stopPropagation();
                          // Slice 2B1 — single-execution guarantee.
                          // The in-flight ref pins this rule for one
                          // synchronous execution. Any additional invocation
                          // of the same handler for the same rule within
                          // the same tick is a no-op. This handles React
                          // 18 StrictMode replays, synthetic-event replays,
                          // rapid-click browser quirks, and any future
                          // accidental double-registration.
                          if (inFlightRulesRef.current.has(s.pricing_rule_id)) {
                            return;
                          }
                          inFlightRulesRef.current.add(s.pricing_rule_id);
                          // Release the guard on the next paint so a
                          // legitimate SECOND user click can still hit
                          // the duplicate-detection path below.
                          setTimeout(() => {
                            inFlightRulesRef.current.delete(s.pricing_rule_id);
                          }, 250);

                          // Preferred source order for the display / line
                          // name: system_type first, saved rule name only
                          // when system_type is absent.
                          const displayName = s.system_type || s.name || "Approved system";
                          const stableId = `pr-${s.pricing_rule_id}`;
                          // Slice 2B1 — Deterministic quantity prefill from
                          // saved job measurements + walkthrough notes.
                          // Only runs when the approved rule's unit is a
                          // recognized roof-area unit (sqft / roofing_square).
                          // NEVER applies waste / pitch / complexity.
                          const wantUnit = s.pricing_type === "installed_selling"
                            ? mapRuleUnitToWant(s.selling_price_unit)
                            : null;
                          const prefill = wantUnit
                            ? extractRoofArea(project, estimate, wantUnit)
                            : null;
                          const line = s.pricing_type === "installed_selling" ? {
                            id: stableId,
                            name: displayName,
                            description: "",
                            // Authoritative pricing shape — server-side
                            // validate_and_normalize_lines will confirm
                            // ownership + exact rate/unit match.
                            pricing_type: "installed_selling",
                            pricing_rule_id: s.pricing_rule_id,
                            selling_price_per_unit: s.selling_price_per_unit,
                            selling_price_unit: s.selling_price_unit,
                            unit: s.selling_price_unit,
                            quantity: prefill ? prefill.quantity : 0,
                            zero_selling_price_confirmed: s.zero_selling_price_confirmed,
                            zero_selling_price_confirmed_at: s.zero_selling_price_confirmed_at || "",
                            // Contractor-internal audit fields — provenance
                            // for the auto-populated quantity. Scrubbed from
                            // every customer-facing surface by
                            // customer_safety.FORBIDDEN_INTERNAL_FIELDS.
                            quantity_source: prefill ? prefill.sourceKey : null,
                            quantity_source_text: prefill ? prefill.sourceText : null,
                            quantity_source_value: prefill ? prefill.sourceValue : null,
                            quantity_source_unit: prefill ? prefill.sourceUnit : null,
                            quantity_source_display_value: prefill ? prefill.quantity : null,
                            quantity_converted: prefill ? prefill.converted : false,
                            quantity_conversion_rule: prefill ? prefill.conversionRule : null,
                            quantity_contractor_edited: false,
                            // Suppress every non-installed shape so
                            // downstream row renderers cannot mis-detect
                            // this as labor-only / M+L / markup pricing.
                            material_required: false,
                            labor_required: false,
                            material_qty: 0, material_cost: 0,
                            labor_qty: 0, labor_unit_cost: 0,
                            labor_hours: 0, labor_cost: 0,
                            labor_basis: "",
                            markup_pct: 0,
                            needs_pricing: false,
                            needs_material_pricing: false,
                            is_installed_selling: true,   // display-hint for the row renderer
                            total: 0,
                          } : s.pricing_type === "fixed_price" ? {
                            id: stableId,
                            name: displayName,
                            description: "",
                            pricing_type: "fixed_price",
                            pricing_rule_id: s.pricing_rule_id,
                            total: s.flat_price_amount || 0,
                            unit: "job",
                            quantity: 1,
                            material_required: false,
                            labor_required: false,
                            markup_pct: 0,
                            needs_pricing: false,
                            needs_material_pricing: false,
                            is_flat_price: true,
                          } : {
                            id: stableId,
                            name: displayName,
                            pricing_type: s.pricing_type || "unit_priced",
                            pricing_rule_id: s.pricing_rule_id,
                            labor_basis: s.labor_basis || "hour",
                            labor_unit_cost: s.labor_unit_cost || 0,
                            labor_qty: 0,
                            material_qty: 0,
                            material_cost: 0,
                            markup_pct: s.markup_pct,
                            material_required: true,
                            labor_required: true,
                            quantity: 1,
                            unit: "each",
                            total: 0,
                          };
                          // Toast decision is delegated to `insertLine`
                          // via callbacks so it cannot mis-fire against a
                          // stale-closure snapshot of estimate.line_items.
                          // Single source of truth for the outcome is the
                          // functional setState updater inside Estimates.jsx.
                          insertLine(line, {
                            onInserted: () => {
                              if (prefill) {
                                toast.success(
                                  t("roofing_builder.toast_approved_added_prefill", displayName, prefill.sourceText, prefill.sourceLabel),
                                  { duration: 5000 },
                                );
                              } else {
                                toast.success(t("roofing_builder.toast_approved_added", displayName));
                              }
                            },
                            onDuplicate: () => {
                              toast.info(t("roofing_builder.toast_approved_duplicate", displayName));
                            },
                          });
                        }}
                        className="text-xs px-2 py-1 rounded jc-btn-primary shrink-0"
                        data-testid={`roofing-approved-use-${s.pricing_rule_id}`}>
                        {t("roofing_builder.use_approved")}
                      </button>
                    </div>
                    {s.pricing_type === "installed_selling" && (
                      <div className="mt-2 rounded border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-900 leading-relaxed"
                           data-testid={`roofing-approved-installed-copy-${s.pricing_rule_id}`}>
                        <div className="font-semibold">
                          {t("roofing_builder.installed_headline")}
                        </div>
                        <div className="mt-0.5">
                          {t("roofing_builder.installed_body")}
                        </div>
                        <div className="mt-0.5 text-emerald-800">
                          {t("roofing_builder.installed_note")}
                        </div>
                      </div>
                    )}
                    <div className="mt-2 text-[11px] text-[var(--jc-text-soft)] leading-relaxed">
                      {t("roofing_builder.approved_this_job_prefix")}<b>{t("roofing_builder.approved_this_job_bold")}</b>{t("roofing_builder.approved_this_job_suffix")}<a href="/app/setup?step=trade_setup" className="underline">{t("roofing_builder.approved_this_job_link")}</a>.
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-2">
              {t("roofing_builder.q_system")}
            </label>
            <select
              value={roofSystemId}
              onChange={(e) => setField({ roof_system_type: e.target.value || null })}
              data-testid="roofing-system-select"
              className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white"
            >
              <option value="">{t("roofing_builder.choose_system")}</option>
              {ROOF_SYSTEMS.map((s) => (
                <option key={s.id} value={s.id}>{t(s.labelKey)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-2">
              {t("roofing_builder.q_pricing")}
            </label>
            <div role="radiogroup" aria-label={t("roofing_builder.pricing_radiogroup_label")} className="grid sm:grid-cols-2 gap-2">
              {PRICING_STYLES.map((style) => {
                const active = (pricingStyle || DEFAULT_STYLE) === style.id;
                return (
                  <button
                    key={style.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setField({ estimate_pricing_style: style.id })}
                    data-testid={`roofing-style-${style.id}`}
                    className={`text-left rounded-md border p-3 transition ${
                      active
                        ? "border-[var(--jc-blue)] bg-[var(--jc-blue)]/5 ring-2 ring-[var(--jc-blue)]/20"
                        : "border-[var(--jc-border-strong)] hover:border-[var(--jc-blue)]/50 hover:bg-[var(--jc-bg)]"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        aria-hidden="true"
                        className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 ${
                          active
                            ? "border-[var(--jc-blue)] bg-[var(--jc-blue)]"
                            : "border-[var(--jc-border-strong)]"
                        }`}
                      >
                        {active && <span className="block w-1.5 h-1.5 rounded-full bg-white m-auto mt-[3px]" />}
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-xs text-[var(--jc-text)]">{t(style.labelKey)}</div>
                        <div className="text-[11px] text-[var(--jc-text-soft)] mt-0.5 leading-relaxed">{t(style.descKey)}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* R2 — Labor Package inline row (only for labor_package_materials mode) */}
          {(pricingStyle || DEFAULT_STYLE) === "labor_package_materials" && (
            <div
              data-testid="roofing-labor-package-panel"
              className="rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)]/40 p-4"
            >
              <div className="flex items-start gap-2">
                <Info size={14} className="text-[var(--jc-blue)] mt-0.5 shrink-0" />
                <div className="text-xs text-[var(--jc-text-soft)] leading-relaxed">
                  {system
                    ? <>{t("roofing_builder.labor_pkg_hint_with_system_prefix")}
                      <b className="text-[var(--jc-text)]">{system.labor_package_name}</b>
                      {t("roofing_builder.labor_pkg_hint_with_system_suffix")}</>
                    : <>{t("roofing_builder.labor_pkg_hint_no_system_prefix")}<b>{t("roofing_builder.labor_pkg_hint_no_system_bold")}</b>{t("roofing_builder.labor_pkg_hint_no_system_suffix")}</>}
                </div>
              </div>
              <button
                type="button"
                onClick={addLaborPackage}
                disabled={!system}
                data-testid="roofing-add-labor-package-btn"
                className={`mt-3 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                  alreadyAdded
                    ? "border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] text-[var(--jc-text)]"
                    : "jc-btn-primary"
                }`}
              >
                {alreadyAdded
                  ? (<><ArrowDown size={12} /> {t("roofing_builder.labor_pkg_already_added")}</>)
                  : (<><Plus size={12} /> {t("roofing_builder.labor_pkg_add")}</>)}
              </button>
            </div>
          )}

          {(pricingStyle || DEFAULT_STYLE) === "detailed" && (
            <div
              data-testid="roofing-detailed-hint"
              className="rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)]/40 p-4 text-xs text-[var(--jc-text-soft)] leading-relaxed"
            >
              <b className="text-[var(--jc-text)]">{t("roofing_builder.detailed_hint_bold")}</b>{t("roofing_builder.detailed_hint_body")}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default RoofingSimpleBuilder;
